import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  formatThousands,
  labelForGefahrId,
  type GefahrenFormState,
  type GefahrFormInput,
} from "./gefahrenConfig";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "12px",
    rowGap: "6px",
    alignItems: "baseline",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
  },
  headerStrong: {
    fontWeight: 700,
    color: BRAND.ink,
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
  },
  th: {
    textAlign: "left",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    whiteSpace: "nowrap",
  },
  td: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    color: BRAND.ink,
    verticalAlign: "top",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  tdMuted: {
    color: BRAND.muted,
    fontStyle: "italic",
  },
  rowDiff: {
    backgroundColor: "#fff5e6",
  },
  gefahrCell: {
    fontWeight: 700,
    color: BRAND.ink,
  },
  pill: {
    display: "inline-block",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    ...shorthands.borderRadius("999px"),
  },
  pillOn: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
  },
  pillOff: {
    backgroundColor: BRAND.bg,
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
});

/** Betrag als "2.500.000 €" — leer bleibt ein Gedankenstrich. */
const formatAmount = (digits?: string): string => {
  const f = formatThousands(digits ?? "");
  return f ? `${f} €` : "—";
};

const mv = (input: GefahrFormInput | undefined): string =>
  input?.mietverlust === "ja" ? "ja" : "—";

const FremdvertragVergleichTable = ({
  kuendigungZum,
  vorvertragGefahren,
  vorvertragMvHaftzeit,
  neuGefahren,
  neuMvHaftzeit,
}: {
  kuendigungZum?: string;
  vorvertragGefahren: GefahrenFormState;
  vorvertragMvHaftzeit?: string;
  neuGefahren: GefahrenFormState;
  neuMvHaftzeit?: string;
}) => {
  const styles = useStyles();

  const allIds = useMemo(() => {
    const set = new Set<string>();
    for (const [id, v] of Object.entries(vorvertragGefahren)) if (v?.sollVersichertWerden === "ja") set.add(id);
    for (const [id, v] of Object.entries(neuGefahren)) if (v?.sollVersichertWerden === "ja") set.add(id);
    return Array.from(set);
  }, [vorvertragGefahren, neuGefahren]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.headerStrong}>Kündigung Vorvertrag bis:</span>
        <span>{kuendigungZum || "—"}</span>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} rowSpan={2}>Gefahr</th>
              <th className={styles.th} colSpan={5}>Vorvertrag</th>
              <th className={styles.th} colSpan={5}>Neuer Vertrag</th>
            </tr>
            <tr>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>HE</th>
              <th className={styles.th}>JHE</th>
              <th className={styles.th}>SB</th>
              <th className={styles.th}>Mietverlust</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>HE</th>
              <th className={styles.th}>JHE</th>
              <th className={styles.th}>SB</th>
              <th className={styles.th}>Mietverlust</th>
            </tr>
          </thead>
          <tbody>
            {allIds.length === 0 && (
              <tr>
                <td className={mergeClasses(styles.td, styles.tdMuted)} colSpan={11}>
                  Noch keine Gefahren ausgewählt.
                </td>
              </tr>
            )}
            {allIds.map((id) => {
              const vorInput = vorvertragGefahren[id];
              const neuInput = neuGefahren[id];
              const inVor = vorInput?.sollVersichertWerden === "ja";
              const inNeu = neuInput?.sollVersichertWerden === "ja";
              const diff =
                inVor !== inNeu ||
                (inVor &&
                  inNeu &&
                  (vorInput?.hoechstentschaedigung !== neuInput?.hoechstentschaedigung ||
                    vorInput?.jahreshoechstentschaedigung !== neuInput?.jahreshoechstentschaedigung ||
                    vorInput?.selbstbeteiligung !== neuInput?.selbstbeteiligung ||
                    vorInput?.mietverlust !== neuInput?.mietverlust));
              return (
                <tr key={id} className={diff ? styles.rowDiff : ""}>
                  <td className={mergeClasses(styles.td, styles.gefahrCell)}>{labelForGefahrId(id)}</td>
                  <td className={styles.td}>
                    <span className={mergeClasses(styles.pill, inVor ? styles.pillOn : styles.pillOff)}>
                      {inVor ? "versichert" : "—"}
                    </span>
                  </td>
                  <td className={styles.td}>{inVor ? formatAmount(vorInput?.hoechstentschaedigung) : "—"}</td>
                  <td className={styles.td}>{inVor ? formatAmount(vorInput?.jahreshoechstentschaedigung) : "—"}</td>
                  <td className={styles.td}>{inVor ? formatAmount(vorInput?.selbstbeteiligung) : "—"}</td>
                  <td className={styles.td}>{inVor ? mv(vorInput) : "—"}</td>
                  <td className={styles.td}>
                    <span className={mergeClasses(styles.pill, inNeu ? styles.pillOn : styles.pillOff)}>
                      {inNeu ? "versichert" : "—"}
                    </span>
                  </td>
                  <td className={styles.td}>{inNeu ? formatAmount(neuInput?.hoechstentschaedigung) : "—"}</td>
                  <td className={styles.td}>{inNeu ? formatAmount(neuInput?.jahreshoechstentschaedigung) : "—"}</td>
                  <td className={styles.td}>{inNeu ? formatAmount(neuInput?.selbstbeteiligung) : "—"}</td>
                  <td className={styles.td}>{inNeu ? mv(neuInput) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.header}>
        <span className={styles.headerStrong}>Mietverlust-Haftzeit Vorvertrag:</span>
        <span>{vorvertragMvHaftzeit ? `${vorvertragMvHaftzeit} Monate` : "—"}</span>
        <span className={styles.headerStrong} style={{ marginLeft: 24 }}>Mietverlust-Haftzeit Neu:</span>
        <span>{neuMvHaftzeit ? `${neuMvHaftzeit} Monate` : "—"}</span>
      </div>
    </div>
  );
};

export default FremdvertragVergleichTable;
